import { useCallback, useState } from "react"
import { retry } from "radash"

import { log } from "@be-miniapp/logging"
import { fitBound } from "@be-miniapp/ui/lib/map"

interface UseMapBoundsOptions {
  pickupLatLng: { lat: number; lng: number }
  dropoffLatLng: { lat: number; lng: number }
  mapRef: React.RefObject<any>
  topOffset?: number
  bottomOffset?: number
}

export interface FareMapFitPadding {
  top: number
  right: number
  bottom: number
  left: number
}

export interface FareMapObstructionOffsets {
  topOffset: number
  bottomOffset: number
}

const FARE_DRAWER_ID = "drawer-content-fare"
const FARE_TOP_OVERLAY_ID = "fare-address-pane-overlay"

function clampOffset(offset: number, viewportHeight: number) {
  return Math.max(0, Math.min(viewportHeight, Math.round(offset)))
}

export function getFareMapObstructionOffsets({
  viewportHeight = window.innerHeight,
  drawerId = FARE_DRAWER_ID,
  topOverlayId = FARE_TOP_OVERLAY_ID,
}: {
  viewportHeight?: number
  drawerId?: string
  topOverlayId?: string
} = {}): FareMapObstructionOffsets {
  const safeViewportHeight = viewportHeight > 0 ? viewportHeight : 844
  const drawerElement = document.getElementById(drawerId)
  const topOverlayElement = document.getElementById(topOverlayId)

  const bottomOffset = drawerElement
    ? clampOffset(
        safeViewportHeight - drawerElement.getBoundingClientRect().top,
        safeViewportHeight
      )
    : 0

  const topOffset = topOverlayElement
    ? clampOffset(
        topOverlayElement.getBoundingClientRect().bottom,
        safeViewportHeight
      )
    : 0

  return {
    topOffset,
    bottomOffset,
  }
}

export function getFareMapFitPadding({
  viewportWidth = window.innerWidth,
  viewportHeight = window.innerHeight,
  topOffset = 0,
  bottomOffset = 0,
}: {
  viewportWidth?: number
  viewportHeight?: number
  topOffset?: number
  bottomOffset?: number
} = {}): FareMapFitPadding {
  const safeViewportWidth = viewportWidth > 0 ? viewportWidth : 390
  const safeViewportHeight = viewportHeight > 0 ? viewportHeight : 844

  const horizontalPadding = Math.min(
    132,
    Math.max(96, Math.round(safeViewportWidth * 0.2))
  )

  const topPadding = Math.min(
    Math.round(safeViewportHeight * 0.3),
    Math.max(
      160,
      Math.round(safeViewportHeight * 0.18),
      Math.round(topOffset + Math.min(72, safeViewportHeight * 0.06))
    )
  )

  const bottomPadding = Math.min(
    Math.round(safeViewportHeight * 0.62),
    Math.max(
      Math.round(safeViewportHeight * 0.38),
      Math.round(bottomOffset + Math.min(96, safeViewportHeight * 0.08))
    )
  )

  return {
    top: topPadding,
    right: horizontalPadding,
    bottom: bottomPadding,
    left: horizontalPadding,
  }
}

export const useMapBounds = ({
  pickupLatLng,
  dropoffLatLng,
  mapRef,
  topOffset = 0,
  bottomOffset = 0,
}: UseMapBoundsOptions) => {
  const [mapReady, setMapReady] = useState(false)

  const tryFitBound = useCallback(() => {
    if (!mapReady) {
      return
    }

    try {
      if (
        !pickupLatLng.lat ||
        !pickupLatLng.lng ||
        !dropoffLatLng.lat ||
        !dropoffLatLng.lng
      ) {
        log({
          category: "fare-map-pan",
          message: "latlng is not ready",
          data: {
            pickupLatLng,
            dropoffLatLng,
          },
          level: "error",
        })
        return
      }

      const bounds = [
        [pickupLatLng.lat, pickupLatLng.lng],
        [dropoffLatLng.lat, dropoffLatLng.lng],
      ]

      setTimeout(() => {
        retry(
          {
            times: 3,
            delay: 500,
          },
          async () => {
            if (!mapRef.current) {
              log({
                category: "fare-map-pan",
                message: "map is not ready",
                data: {
                  pickupLatLng,
                  dropoffLatLng,
                },
                level: "error",
              })

              throw new Error("Map is not ready")
            }

            const obstructionOffsets = getFareMapObstructionOffsets()

            fitBound(
              mapRef.current!,
              bounds,
              getFareMapFitPadding({
                viewportWidth: window.innerWidth,
                viewportHeight: window.innerHeight,
                topOffset: obstructionOffsets.topOffset || topOffset,
                bottomOffset: obstructionOffsets.bottomOffset || bottomOffset,
              })
            )
          }
        )
      }, 400)
    } catch (err) {
      log({
        category: "fare-map-pan",
        level: "error",
        message: "Failed to fit bound",
        data: {
          pickupLatLng,
          dropoffLatLng,
          err,
        },
      })
    }
  }, [pickupLatLng, dropoffLatLng, mapReady, mapRef, topOffset, bottomOffset])

  return {
    mapReady,
    setMapReady,
    tryFitBound,
  }
}
